import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, Clock, Award, MapPin, CheckCircle2, Route, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import DisclosureBanner from "../components/DisclosureBanner";
import DriverSpotlight from "../components/DriverSpotlight";

const HERO_IMG = "/hero-truck.jpg";

export default function Home() {
  return (
    <div>
      <DisclosureBanner />

      {/* HERO */}
      <section className="relative overflow-hidden gradient-radial grain-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-up">
            <img src="/logo.png" alt="Integrity Delivered" data-testid="hero-logo" className="h-28 sm:h-36 lg:h-44 w-auto mb-6 drop-shadow-[0_6px_24px_rgba(0,0,0,0.75)]" />
            <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/30 uppercase tracking-widest text-[10px] px-3 py-1 rounded-full font-semibold">
              Baltimore, MD · Est. Delivery Partner
            </Badge>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] text-white display">
              Delivering with <span className="text-amber-400">integrity.</span><br />
              Every package. Every stop.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
              We are an independent FedEx Ground contracted service provider serving the greater Baltimore region — built on operational excellence, safety, and drivers who take pride in the job.
            </p>
            <div data-testid="hero-mission" className="mt-6 inline-flex flex-wrap items-center gap-x-3 gap-y-1 border-l-4 border-amber-500 pl-4 py-1">
              {["Protect the Service", "Protect the Contract", "Safety Above All"].map((m, i) => (
                <span key={m} className="flex items-center gap-3 text-sm sm:text-base font-extrabold uppercase tracking-wide display text-white">
                  {i > 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}{m}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/apply">
                <Button data-testid="hero-apply-now-btn" size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider px-8">
                  Apply Now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/careers">
                <Button data-testid="hero-join-team-btn" size="lg" variant="outline" className="border-slate-600 text-slate-100 hover:bg-slate-900 uppercase tracking-wider">
                  Join Our Team
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { n: "20M+", l: "Packages Delivered" },
                { n: "3", l: "Counties Covered" },
                { n: "24/7", l: "Operations Support" },
              ].map((s) => (
                <div key={s.l} data-testid={`hero-stat-${s.l.split(' ')[0].toLowerCase()}`}>
                  <div className="text-3xl font-extrabold text-amber-400 display">{s.n}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5 relative fade-up-2">
            <div className="absolute -inset-3 stripe-amber rounded-2xl opacity-30 blur-sm" />
            <img src={HERO_IMG} alt="Delivery driver" className="relative rounded-2xl border border-slate-700 shadow-2xl w-full h-[420px] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-xl hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">Safety-First Fleet</div>
                  <div className="text-xs text-slate-400">DOT compliant · Trained drivers</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DriverSpotlight />

      {/* MISSION */}
      <section data-testid="mission-banner" className="relative overflow-hidden bg-amber-500 text-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-3 text-xs uppercase tracking-[0.3em] font-bold">Our Mission</div>
          <div className="md:col-span-9 grid sm:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Protect the Service", d: "Every package delivered on time, intact, and scanned right." },
              { n: "02", t: "Protect the Contract", d: "We earn FedEx's trust daily through compliance and performance." },
              { n: "03", t: "Safety Above All", d: "No delivery is worth a shortcut. Drivers and the public come first." },
            ].map((m) => (
              <div key={m.n} data-testid={`mission-${m.n}`}>
                <div className="text-xs font-bold opacity-60">{m.n}</div>
                <div className="mt-1 text-xl md:text-2xl font-extrabold uppercase display leading-tight">{m.t}</div>
                <div className="mt-2 text-sm leading-relaxed opacity-80">{m.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-16 md:py-24 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Our Commitments</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold uppercase text-white display">Built on five principles.</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { Icon: ShieldCheck, t: "Safety First", d: "Zero-compromise commitment to driver, package, and public safety." },
              { Icon: Truck, t: "Reliability", d: "On-time performance is the standard, not the exception." },
              { Icon: Award, t: "Excellence", d: "Continuous improvement across every route and every day." },
              { Icon: Users, t: "Accountability", d: "Drivers and leadership answerable to the same high bar." },
              { Icon: CheckCircle2, t: "Integrity", d: "Honest hiring, honest work, honest service." },
            ].map((v, i) => (
              <div key={v.t} data-testid={`value-card-${i}`} className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 hover:border-amber-500/40 transition-colors">
                <v.Icon className="w-6 h-6 text-amber-400" />
                <div className="mt-4 text-white font-bold display uppercase tracking-tight">{v.t}</div>
                <div className="mt-2 text-sm text-slate-400 leading-relaxed">{v.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPPORTUNITIES */}
      <section className="py-16 md:py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Now Hiring</div>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold uppercase text-white display">Three ways to drive with us.</h2>
            </div>
            <Link to="/careers"><Button data-testid="home-view-careers-btn" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-900">All Openings <ArrowRight className="w-4 h-4 ml-1"/></Button></Link>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { Icon: Truck, tag: "Full-Time", t: "Delivery Driver", d: "40+ hrs weekly, benefits eligible, set weekly schedules with paid training.", href: "/apply?position=full_time" },
              { Icon: Award, tag: "Fast Track", t: "Experienced FedEx Driver", d: "Prior FedEx Ground / HD experience: prioritized hiring, top routes.", href: "/apply?position=experienced_fedex" },
              { Icon: Clock, tag: "AVP · Own Vehicle", t: "AVP Priority Delivery", d: "Part-time shifts 9:00 AM – 1:00 PM or 4:00 PM – 8:00 PM using your own qualifying vehicle. $80 guaranteed base + $2 per qualifying on-time stop (20 stops = $120).", href: "/avp" },
            ].map((c, i) => (
              <Link key={c.t} to={c.href} data-testid={`home-opportunity-${i}`} className="group relative rounded-2xl border border-slate-800 bg-slate-900/70 p-7 hover:border-amber-500/60 transition-all">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-400 font-bold">
                  <c.Icon className="w-3.5 h-3.5" /> {c.tag}
                </div>
                <div className="mt-4 text-2xl font-bold display uppercase text-white leading-tight">{c.t}</div>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.d}</p>
                <div className="mt-6 text-amber-400 text-sm font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Apply <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="py-16 md:py-24 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Service Area</div>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold uppercase text-white display">Greater Baltimore.<br /> Every day. Every zip.</h2>
            <p className="mt-5 text-slate-300 leading-relaxed max-w-lg">
              We move packages across the region — from the Inner Harbor to the county suburbs — with dedicated routes and drivers who know the neighborhoods.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-3 max-w-md">
              {["Baltimore City","Baltimore County","Anne Arundel County","Howard County","Harford County","Carroll County"].map((z) => (
                <li key={z} className="flex items-center gap-2 text-sm text-slate-300">
                  <MapPin className="w-4 h-4 text-amber-400" /> {z}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="rounded-2xl border border-slate-800 overflow-hidden bg-slate-900">
              <div className="aspect-[4/3] w-full relative">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-purple-900/10" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Route className="w-32 h-32 text-amber-400/40" strokeWidth={1.2} />
                </div>
                <div className="absolute bottom-4 left-4 right-4 bg-slate-950/80 backdrop-blur border border-slate-700 rounded-lg p-3">
                  <div className="text-xs uppercase tracking-widest text-slate-400">Coverage</div>
                  <div className="text-white font-bold display uppercase">Greater Baltimore Metro</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
